import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { filter } from 'rxjs/operators';
import { MenuItem, MenuLink, MenuSublink } from '../../shared/models';
import { ShipmentUserTypeEnum } from '../../shared/enums/shipment-user-type-enum';
import { LoggedUserService } from './logged-user.service';

@Injectable({
  providedIn: 'root',
})
export class MenuService {
  private menuItemsSource = new BehaviorSubject<MenuItem[]>([]);
  menuItems$ = this.menuItemsSource.asObservable();

  constructor(
    private router: Router,
    private loggedUserService: LoggedUserService,
  ) {
    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe((event: any) => {
      this.updateMenuBasedOnRoute(event.url);
    });
  }

  private updateMenuBasedOnRoute(url: string): void {
    let menuItems: MenuItem[] = [];

    if (url.startsWith('/reports')) menuItems = this.getReportsMenu();
    if (url.startsWith('/unit-commands')) menuItems = this.getSimGlobalMenu();
    if (url.startsWith('/shipment')) menuItems = this.getShipmentMenu();
    if (url.startsWith('/phones-admin')) menuItems = this.getPhoneAdministrationMenu();
    if (url.startsWith('/linker-driver-diagnostics')) menuItems = this.getLinkerDriverDiagnosticsMenu();
    if (url.startsWith('/edi')) menuItems = this.getEdiMenu();
    if (url.startsWith('/tpms')) menuItems = this.getTpmsMenu();
    if (url.startsWith('/cameras')) menuItems = this.getCamerasMenu();

    this.menuItemsSource.next(menuItems);
  }

  private getReportsMenu(): MenuSublink[] {
    const menuItems: MenuSublink[] = [
      { name: 'Dashboard', url: 'reports/dashboard', icon: 'space_dashboard' },
      { name: 'Resumen Unidades', url: 'reports/units-summary', icon: 'summarize' },
      { name: 'Reporte Alertas', url: 'reports/alerts-report', icon: 'notification_important' },
      { name: 'Seguimiento Alertas', url: 'reports/alert-tracking', icon: 'track_changes' },
      { name: 'Reporte Eventos', url: 'reports/events', icon: 'event_note' },
      { name: 'Resumen Niveles Diesel', url: 'reports/fuel-level', icon: 'local_gas_station' },
      { name: 'Niveles Temperatura', url: 'reports/levels-temperature', icon: 'thermostat' },
      { name: 'Carga/Descarga', url: 'reports/load-unload', icon: 'swap_vert' },
      { name: 'Dispositivos Activos', url: 'reports/active-devices', icon: 'sensors' },
      { name: 'Histórico TPMS International', url: 'reports/tpms-historical', permission: 'tpms:view', icon: 'tire_repair' },
    ];

    return menuItems.filter((menuItem) =>
      menuItem.permission ? this.loggedUserService.hasPermission(menuItem.permission) : true,
    );
  }

  private getShipmentMenu(): MenuSublink[] {
    const userInfo = this.loggedUserService.loggedUserInfo;

    if (!userInfo.shipment) return [];

    switch (userInfo.shipment.userType) {
      case ShipmentUserTypeEnum.Carrier:
        return [
          { name: 'Embarques', url: '/shipment/shipments', icon: 'inventory_2' },
          { name: 'Reportes', url: '/shipment/reports', icon: 'description' },
          { name: 'Clientes', url: '/shipment/clients', icon: 'business' },
          { name: 'Compradores', url: '/shipment/customers', icon: 'groups' },
          { name: 'Origen', url: '/shipment/origins', icon: 'trip_origin' },
          { name: 'Destinos', url: '/shipment/destinations', icon: 'place' },
          { name: 'Rutas', url: '/shipment/routes', icon: 'route' },
          { name: 'Fletes', url: '/shipment/freight-classes', icon: 'category' },
          { name: 'Tiempos', url: '/shipment/travel-times', icon: 'schedule' },
          { name: 'Remolques', url: '/shipment/trailers', icon: 'rv_hookup' },
          { name: 'Ofertar', url: '/shipment/offer', icon: 'local_offer' },
        ];
      case ShipmentUserTypeEnum.Client:
        return [
          { name: 'Embarques', url: '/shipment/shipments', icon: 'inventory_2' },
          { name: 'Reportes', url: '/shipment/reports', icon: 'description' },
          { name: 'Compradores', url: '/shipment/customers', icon: 'groups' },
          { name: 'Origen', url: '/shipment/origins', icon: 'trip_origin' },
          { name: 'Destinos', url: '/shipment/destinations', icon: 'place' },
          { name: 'Rutas', url: '/shipment/routes', icon: 'route' },
          { name: 'Ofertar', url: '/shipment/offer', icon: 'local_offer' },
        ];
      case ShipmentUserTypeEnum.Customer:
        return [
          { name: 'Embarques', url: '/shipment/shipments', icon: 'inventory_2' },
          { name: 'Reportes', url: '/shipment/reports', icon: 'description' },
        ];
      default:
        return [];
    }
  }

  private getSimGlobalMenu(): MenuSublink[] {
    const menuItems: MenuSublink[] = [
      { name: 'Sim Global', url: '/unit-commands', icon: 'sim_card' },
      {
        name: 'Reportes',
        url: '/unit-commands/not-billing-sims',
        icon: 'receipt_long',
        permission: 'sim:reports',
      },
    ];

    return menuItems.filter((menuItem) =>
      menuItem.permission
        ? this.loggedUserService.hasPermission(menuItem.permission)
        : true,
    );
  }

  private getPhoneAdministrationMenu(): MenuSublink[] {
    return [
      { name: 'Administración de Teléfonos', url: 'phones-admin/phones-admin', icon: 'phone_iphone' },
      { name: 'Historial', url: '/phones-admin/history', icon: 'history' },
      { name: 'Modelos', url: '/phones-admin/phone-devices', icon: 'devices' },
      { name: 'Incidencias', url: '/phones-admin/incidents', icon: 'report_problem' },
    ];
  }

  private getLinkerDriverDiagnosticsMenu(): MenuSublink[] {
    return [{ name: 'Diagnóstico', url: '/linker-driver-diagnostics', icon: 'build' }];
  }

  private getEdiMenu(): MenuSublink[] {
    const menuItems: MenuSublink[] = [
      {
        name: 'Factura APTIV',
        url: '/edi/aptiv-invoice',
      },
    ];

    return menuItems.filter((menuItem) =>
      menuItem.permission
        ? this.loggedUserService.hasPermission(menuItem.permission)
        : true,
    );
  }

 
  private getTpmsMenu(): MenuSublink[] {
    const menuItems: MenuSublink[] = [
      { name: 'TPMS', url: '/tpms/monitor', icon: 'tire_repair' },
      { name: '5ta Rueda', url: '/tpms/fifth-wheel', icon: 'rv_hookup', permission: 'dashboard:fifth_wheel' },
      {
        name: 'Histórico TPMS International',
        url: '/tpms/tpms-historical',
        icon: 'history',
        permission: 'tpms:view',
      },
    ];

    return menuItems.filter((menuItem) =>
      menuItem.permission ? this.loggedUserService.hasPermission(menuItem.permission) : true,
    );
  }

  private getCamerasMenu(): MenuSublink[] {
    return [
      { name: 'Monitoreo', url: '/cameras/monitoring', icon: 'videocam' },
      { name: 'En vivo', url: '/cameras/live', icon: 'live_tv' },
      { name: 'Historial', url: '/cameras/history', icon: 'video_library' },
      { name: 'Solicitudes', url: '/cameras/requests', icon: 'pending_actions' },
    ];
  }

  getNavigation() {
    const NAVIGATION: MenuLink[] = [
      {
        name: 'Chats',
        shortName: 'Chats',
        url: '/chat',
        icon: 'forum',
        permission: 'chat:monitor-units',
        type: 'chat',
        sublinks: [],
      },
      {
        name: 'Sim Global',
        shortName: 'SIM',
        url: '/unit-commands',
        icon: 'sim_card',
        permission: 'sim:access',
        type: 'globalSim',
        sublinks: this.getSimGlobalMenu(),
      },
      {
        name: 'Shipment',
        shortName: 'Shipment',
        url: '/shipment/shipments',
        icon: 'local_shipping',
        permission: 'shipment:access',
        type: 'shipment',
        sublinks: this.getShipmentMenu(),
      },
      {
        name: 'Reportes',
        shortName: 'Reportes',
        url: '/reports',
        icon: 'description',
        permission: 'reports:access',
        type: 'reports',
        sublinks: this.getReportsMenu(),
      },
      {
        name: 'Admin. de Telefonos',
        shortName: 'Teléfonos',
        url: '/phones-admin',
        icon: 'phone_iphone',
        permission: 'phone:access',
        type: 'phonesAdmin',
        sublinks: this.getPhoneAdministrationMenu(),
      },
      {
        name: 'Diagnostico Linker Driver',
        shortName: 'Diagnóstico',
        url: '/linker-driver-diagnostics',
        icon: 'build',
        permission: 'linker-diagnostic:access',
        type: 'linkerDiagnostic',
        sublinks: this.getLinkerDriverDiagnosticsMenu(),
      },
      {
        name: 'Monitor',
        shortName: 'Monitor',
        url: '/monitor',
        icon: 'monitor',
        permission: 'monitor:view',
        type: 'monitor',
        sublinks: [],
      },
      {
        name: 'Cámaras',
        shortName: 'Cámaras',
        url: '/cameras/monitoring',
        icon: 'video_camera_back',
        // TODO: set to 'cameras:view' once the backend grants it to roles.
        permission: '',
        type: 'cameras',
        sublinks: this.getCamerasMenu(),
      },
      // {
      //   name: 'EDI',
      //   url: '/edi/aptiv-invoice',
      //   icon: 'receipt_long',
      //   permission: 'edi:access',
      //   type: 'edi',
      //   sublinks: this.getEdiMenu(),
      // },
 
      {
        name: 'Dashboards',
        shortName: 'Dashboards',
        url: '/tpms/monitor',
        icon: 'tire_repair',
        permission: 'tpms:view',
        type: 'tpms',
        sublinks: this.getTpmsMenu(),
      },
    ];

    return NAVIGATION;
  }
}